import { Button, HStack, List, ListItem, StackProps } from "@biblioteksentralen/react";
import { Subject } from "@biblioteksentralen/cordata";
import { FormEventHandler, useState } from "react";
import InternalLink from "../../components/InternalLink";
import { getPath } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { CordataSearchFacetTerm } from "@libry-content/integrations";
import { SubjectBadge } from "./works/SubjectBadge";
import { ResolvedPaletteColor } from "@libry-content/common";

const initialMaxSubjects = 20;

export const FacetLinks = ({ facetTerms }: { facetTerms: CordataSearchFacetTerm[] }) => {
  return (
    <SubjectLinksGeneric
      items={facetTerms}
      getLabel={(item) => item.name}
      getKey={(item) => item.value}
      getPath={(item) => getPath({ ...item, type: "FacetTerm" })}
      marginBottom="1rem"
    />
  );
};

export const SubjectLinks = ({
  subjects,
  paletteColor,
  ...chakraProps
}: { subjects: Subject[]; paletteColor?: ResolvedPaletteColor } & StackProps) => {
  const { ts } = useTranslation();

  return (
    <SubjectLinksGeneric
      items={subjects}
      getLabel={(item) => ts(item.name)}
      getKey={(item) => item.id}
      getPath={(item) => getPath({ ...item, type: "Subject" })}
      paletteColor={paletteColor}
      {...chakraProps}
    />
  );
};

type Props<T> = {
  items: T[];

  paletteColor?: ResolvedPaletteColor;

  getLabel: (item: T) => string | undefined;

  getKey: (item: T) => string;

  getPath: (item: T) => string;
} & StackProps;

export const SubjectLinksGeneric = <T,>({
  items,
  paletteColor,
  getLabel,
  getKey,
  getPath,
  ...chakraProps
}: Props<T>) => {
  const { t } = useTranslation();

  const [maxSubjects, setMaxSubjects] = useState<number | undefined>(initialMaxSubjects);

  return (
    <HStack as={List} flexWrap="wrap" spacing="0.5rem" {...chakraProps}>
      {items.slice(0, maxSubjects).map((item) => (
        <ListItem key={getKey(item)}>
          <SubjectBadge as={InternalLink} href={getPath(item)} {...paletteColor?.css}>
            {getLabel(item)}
          </SubjectBadge>
        </ListItem>
      ))}

      {typeof maxSubjects !== "undefined" && maxSubjects < items.length && (
        <SetMaxSubjectsButton onClick={() => setMaxSubjects(undefined)} label={t("Vis alle")} />
      )}

      {typeof maxSubjects === "undefined" && (
        <SetMaxSubjectsButton onClick={() => setMaxSubjects(initialMaxSubjects)} label={t("Vis færre")} />
      )}
    </HStack>
  );
};

const SetMaxSubjectsButton = ({ onClick, label }: { onClick: FormEventHandler; label: string }) => (
  <Button
    variant="secondary"
    fontSize="sm"
    size="sm"
    padding="0.25rem 0.5rem"
    height="unset"
    lineHeight="1"
    borderRadius="3xl"
    onClick={onClick}
  >
    {label}
  </Button>
);
